import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { useAuthContext } from "../../context/auth";
import { useRRHHCalculations } from "../../pages/ER/hooks/useRRHHCalculations";
import { getActivos } from "../../utils/activos";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getDolarValue } from "../../utils/exchangeRateService";
// import { ejecutarMatching } from "../../utils/matchingEngine"; // desactivado temporalmente
import { getMovimientosBancarios, getCuentasBancarias } from "../../utils/movimientosBancarios";
import { getPagosPorMovimientos } from "../../utils/pagosEmpleados";
import { getPipelineCobro } from "../../utils/pipelineCobroSII";
import { getCuotasPagosFuturas, getCuotasCobrosFuturas } from "../../utils/planCobros";
import { getPlazosCobroParaSII } from "../../utils/plazosCobroSII";
import { getProveedoresRecurrentesEntries } from "../../utils/proveedoresRecurrentes";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";
import FactoringModal from "../FactoringModal";
import { Header } from "../Header";
import PlanCuotasModal from "../PlanCuotasModal";
import VincularMovimientoModal from "../VincularMovimientoModal";
import styles from "./FlujoCajaV2.module.scss";

const FILTROS_PROYECCION = [
  { key: "todo", label: "Todo" },
  { key: "cobros", label: "Facturado" },
  { key: "pipeline", label: "Pipeline" },
  { key: "proveedores", label: "Proveedores" },
  { key: "edificios", label: "Edificios" },
  { key: "rrhh", label: "RRHH" },
  { key: "impuestos", label: "Impuestos" },
  { key: "capex", label: "CapEx" },
  { key: "financiamiento", label: "Créditos" },
];

const FILTROS_HISTORICO = [
  { key: "todo", label: "Todo" },
  { key: "ingresos", label: "Ingresos" },
  { key: "egresos", label: "Egresos" },
];

const calcularImpuestos = (emitidas, recibidas, pais) => {
  const hoy = new Date().toISOString().split("T")[0];
  const entries = [];
  const tasaPPM = 0.02;

  const ivaVentasPorMes = {};
  const ivaComprasPorMes = {};
  const ventasNetasPorMes = {};

  for (const f of emitidas) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    ivaVentasPorMes[mes] = (ivaVentasPorMes[mes] || 0) + Number(f.monto_iva || 0);
    ventasNetasPorMes[mes] = (ventasNetasPorMes[mes] || 0) + Number(f.monto_neto || 0);
  }

  for (const f of recibidas) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    ivaComprasPorMes[mes] = (ivaComprasPorMes[mes] || 0) + Number(f.monto_iva || 0);
  }

  const periodos = [...new Set([...Object.keys(ivaVentasPorMes), ...Object.keys(ivaComprasPorMes)])].sort();

  for (const periodo of periodos) {
    const [yyyy, mm] = periodo.split("-");
    const mesSiguiente = parseInt(mm, 10) === 12 ? 1 : parseInt(mm, 10) + 1;
    const añoSiguiente = parseInt(mm, 10) === 12 ? parseInt(yyyy, 10) + 1 : parseInt(yyyy, 10);
    const fechaPago = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;

    if (fechaPago <= hoy) continue;

    const ivaVentas = ivaVentasPorMes[periodo] || 0;
    const ivaCompras = ivaComprasPorMes[periodo] || 0;
    const ivaNeto = ivaVentas - ivaCompras;
    const ventasNetas = ventasNetasPorMes[periodo] || 0;
    const ppm = Math.round(ventasNetas * tasaPPM);

    const labelIVA = pais === "Chile" ? "IVA" : "IGV";

    if (ivaNeto > 0) {
      entries.push({
        id: `iva-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -ivaNeto,
        razon_social_receptor: `${labelIVA} ${periodo}`,
        tipo: "impuesto",
      });
    }

    if (ppm > 0) {
      entries.push({
        id: `ppm-${periodo}`,
        fecha_display: fechaPago,
        importeEnMoneda: -ppm,
        razon_social_receptor: `PPM 2% ${periodo}`,
        tipo: "impuesto",
      });
    }
  }

  const labelIVAEst = pais === "Chile" ? "IVA" : "IGV";
  const periodosConDatos = periodos.filter((p) => ivaVentasPorMes[p] > 0);
  const ultimos3 = periodosConDatos.slice(-3);

  if (ultimos3.length > 0) {
    const promedioIVA = Math.round(
      ultimos3.reduce((sum, p) => {
        const ivaNeto = (ivaVentasPorMes[p] || 0) - (ivaComprasPorMes[p] || 0);
        return sum + Math.max(ivaNeto, 0);
      }, 0) / ultimos3.length
    );

    const promedioPPM = Math.round(
      ultimos3.reduce((sum, p) => sum + Math.round((ventasNetasPorMes[p] || 0) * tasaPPM), 0) / ultimos3.length
    );

    const hoyDate = new Date();
    for (let i = 0; i <= 3; i++) {
      const mesProyectado = new Date(hoyDate.getFullYear(), hoyDate.getMonth() + i, 1);
      const yyyy = mesProyectado.getFullYear();
      const mm = String(mesProyectado.getMonth() + 1).padStart(2, "0");
      const periodoProyectado = `${yyyy}-${mm}`;

      if (ivaVentasPorMes[periodoProyectado] && ivaVentasPorMes[periodoProyectado] > 0) continue;

      const mesPago = mesProyectado.getMonth() + 2 > 12 ? 1 : mesProyectado.getMonth() + 2;
      const añoPago = mesProyectado.getMonth() + 2 > 12 ? yyyy + 1 : yyyy;
      const fechaPago = `${añoPago}-${String(mesPago).padStart(2, "0")}-12`;

      if (promedioIVA > 0) {
        entries.push({
          id: `iva-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioIVA,
          razon_social_receptor: `${labelIVAEst} ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }

      if (promedioPPM > 0) {
        entries.push({
          id: `ppm-est-${periodoProyectado}`,
          fecha_display: fechaPago,
          importeEnMoneda: -promedioPPM,
          razon_social_receptor: `PPM 2% ${periodoProyectado}`,
          tipo: "impuesto",
          estimado: true,
        });
      }
    }
  }

  return entries;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  let f = fechaStr;
  if (f.includes("T")) f = f.substring(0, 10);
  const [, month, day] = f.split("-");
  return `${day}/${month}`;
};

const FlujoCajaV2 = () => {
  const { pais } = useAuthContext();
  const { loaded: ratesLoaded } = useExchangeRates(pais);
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";
  const simboloLocal = pais === "Chile" ? "$" : "S/";
  const [monedaVista, setMonedaVista] = useState(monedaLocal);
  const [filtrosActivos, setFiltrosActivos] = useState(["todo"]);
  const [filtroHistorico, setFiltroHistorico] = useState("todo");
  const [filtroTiempo, setFiltroTiempo] = useState("ytd");

  const toggleFiltro = (filtro) => {
    if (filtro === "todo") {
      setFiltrosActivos(["todo"]);
      return;
    }
    setFiltrosActivos((prev) => {
      const sinTodo = prev.filter((f) => f !== "todo");
      if (sinTodo.includes(filtro)) {
        const nuevo = sinTodo.filter((f) => f !== filtro);
        return nuevo.length === 0 ? ["todo"] : nuevo;
      }
      return [...sinTodo, filtro];
    });
  };

  const [movBancarios, setMovBancarios] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [proveedoresPendientes, setProveedoresPendientes] = useState([]);
  const [proveedoresRecurrentes, setProveedoresRecurrentes] = useState([]);
  const [impuestosEntries, setImpuestosEntries] = useState([]);
  const [capexEntries, setCapexEntries] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [facturaFactoring, setFacturaFactoring] = useState(null);
  const [planCuotasModal, setPlanCuotasModal] = useState(null);
  const [vincularModal, setVincularModal] = useState(null);
  const [matchesMap, setMatchesMap] = useState({});
  const [confirmarMatchModal, setConfirmarMatchModal] = useState(null);
  const [cuotasPagosPendientes, setCuotasPagosPendientes] = useState([]);
  const [cuotasCobrosPendientes, setCuotasCobrosPendientes] = useState([]);
  const [pagosEmpleadosRegistrados, setPagosEmpleadosRegistrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  const currentYear = new Date().getFullYear();
  const { empleadosRRHH, isEmpleadosRRHHLoading, getSueldoBaseVigente } = useRRHHCalculations(
    pais,
    currentYear,
    12,
    [],
    []
  );

  const pagosMovIds = useMemo(
    () => new Set(pagosEmpleadosRegistrados.map((p) => p.movimiento_bancario_id).filter(Boolean)),
    [pagosEmpleadosRegistrados]
  );

  const pagadoLiquidoPorEmpMes = useMemo(() => {
    const map = {};
    for (const p of pagosEmpleadosRegistrados) {
      if (p.tipo !== "sueldo" && p.tipo !== "adelanto") continue;
      if (!p.empleado_id || !p.periodo_año || !p.periodo_mes) continue;
      const key = `${p.empleado_id}-${p.periodo_año}-${p.periodo_mes}`;
      map[key] = (map[key] || 0) + Number(p.monto || 0);
    }
    return map;
  }, [pagosEmpleadosRegistrados]);

  const rrhhEntries = useMemo(() => {
    if (isEmpleadosRRHHLoading || !empleadosRRHH?.length) return [];
    const hoy = new Date().toISOString().split("T")[0];
    const año = new Date().getFullYear();

    const DESCUENTO_EMPLEADO_CL = 0.1827;
    const CARGA_EMPLEADOR_CL = 0.052;
    const DESCUENTO_EMPLEADO_PE = 0.13;
    const CARGA_EMPLEADOR_PE = 0.09;

    const entries = [];

    for (let mesIdx = 0; mesIdx < 12; mesIdx++) {
      const ultimoDiaMes = new Date(año, mesIdx + 1, 0).toISOString().split("T")[0];
      const mesSiguiente = mesIdx === 11 ? 1 : mesIdx + 2;
      const añoSiguiente = mesIdx === 11 ? año + 1 : año;
      const dia12SiguienteMes = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;

      if (ultimoDiaMes <= hoy && dia12SiguienteMes <= hoy) continue;

      for (const emp of empleadosRRHH) {
        const mesStr = `${año}-${String(mesIdx + 1).padStart(2, "0")}-01`;
        if (emp.egreso_fecha && emp.egreso_fecha < mesStr) continue;

        const bruto = getSueldoBaseVigente(emp, mesIdx) || 0;
        if (bruto <= 0) continue;

        const nombreRaw = `${emp.nombre || ""} ${emp.apellido || ""}`.trim();
        const nombre = nombreRaw
          .toLowerCase()
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

        const yaPagado = pagadoLiquidoPorEmpMes[`${emp.id}-${año}-${mesIdx + 1}`] || 0;

        if (pais === "Chile") {
          const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_CL));
          const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
          const previred = Math.round(bruto * (DESCUENTO_EMPLEADO_CL + CARGA_EMPLEADOR_CL));

          if (ultimoDiaMes > hoy && saldoLiquido > 0) {
            entries.push({
              id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
              fecha_display: ultimoDiaMes,
              importeEnMoneda: -saldoLiquido,
              razon_social_receptor: `RRHH | ${nombre}${yaPagado > 0 ? " (saldo)" : ""}`,
              tipo: "rrhh",
            });
          }

          if (dia12SiguienteMes > hoy) {
            entries.push({
              id: `rrhh-previred-${emp.id}-${año}-${mesIdx}`,
              fecha_display: dia12SiguienteMes,
              importeEnMoneda: -previred,
              razon_social_receptor: `PREVIRED | ${nombre}`,
              tipo: "rrhh",
            });
          }
        } else {
          const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_PE));
          const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
          const essalud = Math.round(bruto * CARGA_EMPLEADOR_PE);

          if (ultimoDiaMes > hoy && saldoLiquido > 0) {
            entries.push({
              id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
              fecha_display: ultimoDiaMes,
              importeEnMoneda: -saldoLiquido,
              razon_social_receptor: `RRHH | ${nombre}${yaPagado > 0 ? " (saldo)" : ""}`,
              tipo: "rrhh",
            });
          }
          if (ultimoDiaMes > hoy) {
            entries.push({
              id: `rrhh-essalud-${emp.id}-${año}-${mesIdx}`,
              fecha_display: dia12SiguienteMes,
              importeEnMoneda: -essalud,
              razon_social_receptor: `EsSalud | ${nombre}`,
              tipo: "rrhh",
            });
          }
        }
      }
    }

    return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
  }, [empleadosRRHH, isEmpleadosRRHHLoading, pais, getSueldoBaseVigente, pagadoLiquidoPorEmpMes]);

  const cargarDatos = useCallback(async () => {
    if (!pais || pais === "Region" || !ratesLoaded) return;
    setLoading(true);
    try {
      const [movs, ctas, emitidas, pipelineData, recibidas, recurrentes, edificiosData, cuotasPagos, cuotasCobros] =
        await Promise.all([
          getMovimientosBancarios(pais, {
            fechaDesde: "2026-01-01",
          }),
          getCuentasBancarias(pais),
          getFacturasEmitidas(pais, { estado: "vinculada" }),
          getPipelineCobro(pais, monedaLocal),
          getFacturasRecibidas(pais, { estado: "vinculada" }),
          getProveedoresRecurrentesEntries(pais, monedaLocal),
          client.from("edificios").select("id, edificio").eq("pais", pais),
          getCuotasPagosFuturas(pais),
          getCuotasCobrosFuturas(pais),
        ]);
      const ordenados = [...movs].sort((a, b) => b.fecha.localeCompare(a.fecha));
      setMovBancarios(ordenados);
      setCuentas(ctas);
      try {
        const pagosEmp = await getPagosPorMovimientos(ordenados.map((m) => m.id));
        setPagosEmpleadosRegistrados(pagosEmp);
      } catch {
        setPagosEmpleadosRegistrados([]);
      }
      setPipeline(pipelineData);
      setProveedoresPendientes(recibidas);
      setProveedoresRecurrentes(recurrentes);
      setImpuestosEntries(calcularImpuestos(emitidas, recibidas, pais));
      setEdificiosMap(Object.fromEntries((edificiosData.data || []).map((e) => [e.id, e.edificio])));
      setCuotasPagosPendientes(cuotasPagos || []);
      setCuotasCobrosPendientes(cuotasCobros || []);
      const capex = await getActivos(pais, { es_planificado: true });
      setCapexEntries(capex);
      const plazosMap = await getPlazosCobroParaSII(emitidas);
      const emitidasConPlazos = emitidas.map((f) => {
        const key = `${f.venta_tipo}-${f.venta_ref_id}-${f.venta_ref_index ?? ""}`;
        const plazos = plazosMap.get(key) || { plazocobro: null, facturafechacobro: null };
        return { ...f, ...plazos };
      });
      setPendientesCobro(emitidasConPlazos);

      // Matching automático — desactivado temporalmente
      // const noventaDiasAtras = new Date();
      // noventaDiasAtras.setDate(noventaDiasAtras.getDate() - 90);
      // const fechaMinMatch = noventaDiasAtras.toISOString().split("T")[0];
      // const sinVincular = ordenados.filter(
      //   (m) => !m.factura_sii_emitida_id && !m.factura_sii_recibida_id && m.fecha >= fechaMinMatch
      // );
      // if (sinVincular.length > 0) {
      //   try {
      //     const matches = await ejecutarMatching(pais, sinVincular);
      //     const mapa = {};
      //     for (const match of matches) {
      //       if (match.match_razon === "folio_en_descripcion") continue;
      //       if (match.match_confianza < 0.95) continue;
      //       if (!match.match_factura_id) continue;
      //       if (!["factura_sii_emitida", "factura_sii_recibida"].includes(match.match_factura_tipo)) continue;
      //       const key = match.match_factura_tipo === "factura_sii_emitida"
      //         ? `e_${match.match_factura_id}` : `r_${match.match_factura_id}`;
      //       if (!mapa[key] || match.match_confianza > mapa[key].confianza) {
      //         const movBancario = sinVincular.find((m) => m.id === match.movimiento_id);
      //         if (!movBancario) continue;
      //         mapa[key] = {
      //           movimiento_id: match.movimiento_id,
      //           confianza: match.match_confianza,
      //           fecha: movBancario.fecha,
      //           monto: movBancario.monto,
      //           descripcion: movBancario.descripcion_raw,
      //           razon: match.match_razon,
      //         };
      //       }
      //     }
      //     setMatchesMap(mapa);
      //   } catch (err) {
      //     console.error("Error en matching:", err);
      //   }
      // }
      setMatchesMap({});
    } catch {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [pais, monedaLocal, ratesLoaded]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleSync = useCallback(async () => {
    setSincronizando(true);
    try {
      const hoy = new Date();
      const desde = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-01`;
      const hasta = hoy.toISOString().split("T")[0];
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/clay-import?pais=${pais}&desde=${desde}&hasta=${hasta}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const totalNuevos = (data.resultados || []).reduce((s, r) => s + (r.nuevos || 0), 0);
      toast.success(`Cuentas actualizadas: ${totalNuevos} movimientos nuevos`);
      await cargarDatos();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando:", err);
      toast.error("Error al actualizar cuentas");
    } finally {
      setSincronizando(false);
    }
  }, [pais, cargarDatos]);

  const saldoActualLocal = useMemo(
    () => cuentas.filter((c) => c.moneda === monedaLocal).reduce((sum, c) => sum + Number(c.saldo_actual || 0), 0),
    [cuentas, monedaLocal]
  );

  const saldoActualUSD = useMemo(
    () => cuentas.filter((c) => c.moneda === "USD").reduce((sum, c) => sum + Number(c.saldo_actual || 0), 0),
    [cuentas]
  );

  const saldoActual = monedaVista === "USD" ? saldoActualUSD : saldoActualLocal;

  const rangosFecha = useMemo(() => {
    const hoy = new Date();
    const hoyStr = hoy.toISOString().split("T")[0];
    const diaSemana = hoy.getDay() === 0 ? 7 : hoy.getDay();
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - diaSemana + 1);
    const lunesStr = lunes.toISOString().split("T")[0];
    const meses = [];
    for (let i = 1; i <= 3; i++) {
      const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      meses.push({
        key: `mes_${i}`,
        label: d.toLocaleDateString("es", { month: "long" }).replace(/^\w/, (c) => c.toUpperCase()),
        desde: d.toISOString().split("T")[0],
        hasta: new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split("T")[0],
      });
    }
    return {
      hoy: { desde: hoyStr, hasta: hoyStr },
      esta_semana: { desde: lunesStr, hasta: hoyStr },
      este_mes: { desde: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-01`, hasta: hoyStr },
      ytd: { desde: `${hoy.getFullYear()}-01-01`, hasta: hoyStr },
      meses,
    };
  }, []);

  const movimientosFiltrados = useMemo(() => {
    const cuentaIds = cuentas.filter((c) => c.moneda === monedaVista).map((c) => c.id);
    let lista = movBancarios.filter((m) => cuentaIds.includes(m.cuenta_bancaria_id));
    if (filtroHistorico === "ingresos") lista = lista.filter((m) => m.tipo === "ingreso");
    if (filtroHistorico === "egresos") lista = lista.filter((m) => m.tipo === "egreso");
    const rango =
      filtroTiempo === "hoy"
        ? rangosFecha.hoy
        : filtroTiempo === "esta_semana"
          ? rangosFecha.esta_semana
          : filtroTiempo === "este_mes"
            ? rangosFecha.este_mes
            : filtroTiempo === "ytd"
              ? rangosFecha.ytd
              : rangosFecha.meses.find((m) => m.key === filtroTiempo);
    if (rango) lista = lista.filter((m) => m.fecha >= rango.desde && m.fecha <= rango.hasta);
    let saldo = monedaVista === "USD" ? saldoActualUSD : saldoActualLocal;
    return lista.map((m) => {
      const entry = { ...m, saldo_calculado: saldo };
      if (m.tipo === "ingreso") saldo -= Number(m.monto);
      else saldo += Number(m.monto);
      return entry;
    });
  }, [
    movBancarios,
    cuentas,
    monedaVista,
    saldoActualLocal,
    saldoActualUSD,
    filtroHistorico,
    filtroTiempo,
    rangosFecha,
  ]);

  const proyeccionesFuturas = useMemo(() => {
    const hoy = new Date().toISOString().split("T")[0];
    const mostrarTodo = filtrosActivos.includes("todo");
    const mostrarFacturado = mostrarTodo || filtrosActivos.includes("cobros");
    const mostrarPipeline = mostrarTodo || filtrosActivos.includes("pipeline");
    const mostrarProveedores = mostrarTodo || filtrosActivos.includes("proveedores");
    const mostrarRRHH = mostrarTodo || filtrosActivos.includes("rrhh");
    const mostrarImpuestos = mostrarTodo || filtrosActivos.includes("impuestos");
    const esUSD = monedaVista === "USD";

    const mapFacturado = (f) => {
      let importeEnMoneda = Number(f.monto_total || 0);
      if (esUSD) {
        const tasa = getDolarValue(f.fecha_emision, pais) || 1;
        importeEnMoneda = Math.round(importeEnMoneda / tasa);
      }
      return {
        ...f,
        fecha_display: f.plazocobro || null,
        importeEnMoneda,
        tipo: "facturado",
        folio: f.folio,
      };
    };
    const mapPipeline = (p) => ({
      ...p,
      fecha_display: p.fecha_estimada,
      importeEnMoneda: esUSD ? (p.moneda_dolar ? p.importe : 0) : p.moneda_dolar ? p.importeCLP : p.importe,
      razon_social_receptor: p.cliente,
      tipo: "pipeline",
    });
    const facturadoFiltrado = pendientesCobro
      .filter((f) => !f.facturafechacobro && (!f.plazocobro || f.plazocobro > hoy))
      .filter((f) => f.estado_factoring !== "cedida")
      .filter((f) => (esUSD ? f.moneda_dolar_origen === true : f.moneda_dolar_origen !== true));
    // Para cada factura emitida, verificar si tiene cuotas de cobro
    const facturadoEntries = [];
    if (mostrarFacturado) {
      for (const f of facturadoFiltrado) {
        const cuotasFactura = cuotasCobrosPendientes.filter((c) => c.factura_sii_emitida_id === f.id);
        if (cuotasFactura.length > 0) {
          cuotasFactura.forEach((c) => {
            facturadoEntries.push({
              ...f,
              fecha_display: c.fecha_vencimiento,
              importeEnMoneda: Number(c.monto_cuota),
              razon_social_receptor: f.razon_social_receptor,
              tipo: "facturado",
              folio: f.folio,
              cuota_actual: c.numero_cuota,
              total_cuotas: cuotasFactura.length,
            });
          });
        } else {
          facturadoEntries.push(mapFacturado(f));
        }
      }
    }

    const pipe = mostrarPipeline ? pipeline.map(mapPipeline).filter((p) => p.importeEnMoneda > 0) : [];

    // Proveedores: si la factura tiene cuotas pendientes, mostrar cada cuota por separado
    const provsFiltrados = proveedoresPendientes
      .filter((f) => !f.movimiento_bancario_id && (!f.plazopago || f.plazopago > hoy))
      .filter(() => !esUSD);
    const provsEntries = [];
    for (const f of provsFiltrados) {
      const cuotasFactura = cuotasPagosPendientes.filter((c) => c.factura_sii_recibida_id === f.id);
      if (cuotasFactura.length > 0) {
        cuotasFactura.forEach((c) => {
          provsEntries.push({
            ...f,
            fecha_display: c.fecha_vencimiento,
            importeEnMoneda: -Number(c.monto_cuota),
            razon_social_receptor: f.razon_social_emisor,
            tipo: "proveedor",
            folio: f.folio,
            cuota_actual: c.numero_cuota,
            total_cuotas: cuotasFactura.length,
          });
        });
      } else {
        provsEntries.push({
          ...f,
          fecha_display: f.plazopago || null,
          importeEnMoneda: -Number(f.monto_total || 0),
          razon_social_receptor: f.razon_social_emisor,
          tipo: "proveedor",
          folio: f.folio,
        });
      }
    }
    const provsRecurrentesFiltrados = proveedoresRecurrentes.filter((p) =>
      esUSD ? p.moneda_recurrente === "USD" : p.moneda_recurrente !== "USD"
    );
    const provs = mostrarProveedores ? [...provsEntries, ...provsRecurrentesFiltrados] : [];

    const rrhh = mostrarRRHH && !esUSD ? rrhhEntries : [];

    const impuestos = mostrarImpuestos && !esUSD ? impuestosEntries : [];

    const mostrarCapex = mostrarTodo || filtrosActivos.includes("capex");
    const mapCapex = (a) => ({
      id: `capex-${a.id}`,
      fecha_display: a.fecha_planificada,
      importeEnMoneda: -Number(a.costo_unitario),
      razon_social_receptor: [
        a.destino_id ? edificiosMap[a.destino_id] || `Edificio #${a.destino_id}` : null,
        a.tipo_activo?.toUpperCase(),
        a.proveedor?.nombre || null,
      ]
        .filter(Boolean)
        .join(" | "),
      tipo: "capex",
    });
    const capexFiltrado = capexEntries
      .filter((a) => a.fecha_planificada && a.fecha_planificada > hoy)
      .filter((a) => (esUSD ? a.moneda === "USD" : a.moneda !== "USD"));
    const capex = mostrarCapex ? capexFiltrado.map(mapCapex) : [];

    const todos = [...facturadoEntries, ...pipe, ...provs, ...rrhh, ...impuestos, ...capex].sort((a, b) =>
      (a.fecha_display || "9999").localeCompare(b.fecha_display || "9999")
    );

    let saldo = esUSD ? saldoActualUSD : saldoActualLocal;
    return todos.map((f) => {
      saldo += f.importeEnMoneda;
      return { ...f, saldo_proyectado: saldo };
    });
  }, [
    pendientesCobro,
    pipeline,
    proveedoresPendientes,
    proveedoresRecurrentes,
    cuotasPagosPendientes,
    cuotasCobrosPendientes,
    rrhhEntries,
    impuestosEntries,
    capexEntries,
    edificiosMap,
    filtrosActivos,
    saldoActualLocal,
    saldoActualUSD,
    monedaVista,
  ]);

  const gridRef = useRef(null);

  const fmt = (n) =>
    monedaVista === "USD"
      ? `US$ ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`
      : `${simboloLocal} ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

  const pendientesVincular = movBancarios.filter(
    (m) => !m.factura_sii_emitida_id && !m.factura_sii_recibida_id && !pagosMovIds.has(m.id) && m.estado !== "ignorado"
  ).length;

  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={
          <div
            style={{
              display: "flex",
              gap: 0,
              borderRadius: 6,
              overflow: "hidden",
              border: "1px solid #dee2e6",
              marginLeft: 150,
            }}
          >
            {[monedaLocal, "USD"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMonedaVista(m)}
                style={{
                  padding: "6px 18px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  background: monedaVista === m ? "#2c3e50" : "transparent",
                  color: monedaVista === m ? "#fff" : "#666",
                }}
              >
                {m}
              </button>
            ))}
          </div>
        }
      >
        <div className={styles.headerStats}>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Saldo actual</div>
            <div className={styles.statValue}>{fmt(saldoActual)}</div>
          </div>
          <button
            type="button"
            onClick={handleSync}
            disabled={sincronizando}
            style={{
              padding: "6px 14px",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #2c3e50",
              borderRadius: 6,
              background: sincronizando ? "#95a5a6" : "#2c3e50",
              color: "#fff",
              cursor: sincronizando ? "not-allowed" : "pointer",
              marginLeft: 12,
            }}
          >
            {sincronizando ? "Actualizando..." : "Actualizar cuentas"}
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        <div className={styles.grid} ref={gridRef}>
          <div className={styles.connector} />

          {/* Panel izquierdo */}
          <div className={styles.panelWrapper}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#2c3e50",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Movimientos históricos</span>
              {pendientesVincular > 0 && (
                <span
                  style={{
                    background: "#c62828",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    minWidth: 20,
                    height: 20,
                    borderRadius: 10,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 5px",
                  }}
                >
                  {pendientesVincular}
                </span>
              )}
            </div>
            <div className={styles.filterBar} style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 4 }}>
                {FILTROS_HISTORICO.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroHistorico(f.key)}
                    className={`${styles.filterBtn} ${filtroHistorico === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {[
                  { key: "hoy", label: "Hoy" },
                  { key: "esta_semana", label: "Semana" },
                  {
                    key: "este_mes",
                    label: new Date()
                      .toLocaleDateString("es", { month: "long" })
                      .replace(/^\w/, (c) => c.toUpperCase()),
                  },
                  ...rangosFecha.meses.map((m) => ({ key: m.key, label: m.label })),
                  { key: "ytd", label: "YTD" },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroTiempo(f.key)}
                    className={`${styles.filterBtn} ${filtroTiempo === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {loading ? (
                <div className={styles.empty}>Cargando...</div>
              ) : movimientosFiltrados.length === 0 ? (
                <div className={styles.empty}>Sin movimientos</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Movimiento</th>
                      <th>Folio</th>
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosFiltrados.map((m) => (
                      <tr key={m.id}>
                        <td className={styles.fecha}>{formatFecha(m.fecha)}</td>
                        <td className={styles.descripcion} title={m.descripcion_raw}>
                          {m.descripcion_raw}
                        </td>
                        <td className={styles.folio}>
                          {m.factura_emitida?.folio || m.factura_recibida?.folio || m.numero_documento ? (
                            m.factura_emitida?.folio || m.factura_recibida?.folio || m.numero_documento
                          ) : pagosMovIds.has(m.id) ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#27ae60",
                                color: "#fff",
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                width: 18,
                                height: 18,
                                borderRadius: 4,
                              }}
                              title="Pago a empleado"
                            >
                              E
                            </span>
                          ) : m.factura_sii_emitida_id || m.factura_sii_recibida_id || m.estado === "ignorado" ? (
                            "—"
                          ) : (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#e67e22",
                                color: "#fff",
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                width: 18,
                                height: 18,
                                borderRadius: 4,
                              }}
                              title="Pendiente de vincular"
                            >
                              P
                            </span>
                          )}
                        </td>
                        <td className={`${styles.right} ${m.tipo === "ingreso" ? styles.ingreso : styles.egreso}`}>
                          {fmt(m.monto)}
                        </td>
                        <td className={styles.right}>{fmt(m.saldo_calculado)}</td>
                        <td className={styles.accion}>
                          <FaEdit
                            title="Vincular"
                            style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setVincularModal(m);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Panel derecho */}
          <div className={styles.panelWrapper}>
            <div className={styles.panelHeader} style={{ background: "#27ae60" }}>
              Proyección futura
            </div>
            <div className={styles.filterBar}>
              {FILTROS_PROYECCION.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => toggleFiltro(f.key)}
                  className={`${styles.filterBtn} ${filtrosActivos.includes(f.key) ? styles.filterBtnActive : ""}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {proyeccionesFuturas.length === 0 ? (
                <div className={styles.empty}>Sin proyecciones</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>Folio</th>
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proyeccionesFuturas.map((f, i) => (
                      <tr
                        key={`${f.id}-${i}`}
                        style={{ cursor: f.tipo === "facturado" ? "pointer" : "default" }}
                        onClick={() => (f.tipo === "facturado" ? setFacturaFactoring(f) : null)}
                      >
                        <td className={`${styles.fecha} ${!f.fecha_display ? styles.sinFecha : ""}`}>
                          {f.fecha_display ? formatFecha(f.fecha_display) : "Sin fecha"}
                        </td>
                        <td className={styles.descripcion} title={f.razon_social_receptor}>
                          {f.razon_social_receptor || "—"}
                          {f.estimado && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: "0.68rem",
                                background: "#f39c12",
                                color: "#fff",
                                borderRadius: 3,
                                padding: "1px 5px",
                              }}
                            >
                              Estimado
                            </span>
                          )}
                        </td>
                        <td className={styles.folio}>
                          {(() => {
                            const match = matchesMap[`e_${f.id}`] || matchesMap[`r_${f.id}`];
                            const folio = f.folio
                              ? f.cuota_actual
                                ? `${f.folio} (${f.cuota_actual}/${f.total_cuotas})`
                                : `${f.folio}`
                              : null;
                            if (match) {
                              return (
                                <span
                                  title={`Match ${match.confianza >= 0.95 ? "alto" : "medio"} — ${match.fecha}`}
                                  style={{
                                    cursor: "pointer",
                                    color: match.confianza >= 0.95 ? "#27ae60" : "#e67e22",
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    border: `1px solid ${match.confianza >= 0.95 ? "#27ae60" : "#e67e22"}`,
                                    borderRadius: 3,
                                    padding: "1px 5px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmarMatchModal({ proyeccion: f, match });
                                  }}
                                >
                                  {folio || "Match"} ✓
                                </span>
                              );
                            }
                            return folio || "—";
                          })()}
                        </td>
                        <td
                          className={`${styles.right} ${
                            f.tipo === "pipeline"
                              ? styles.pipeline
                              : f.importeEnMoneda < 0
                                ? styles.egreso
                                : styles.ingreso
                          }`}
                        >
                          {fmt(Math.abs(f.importeEnMoneda))}
                        </td>
                        <td className={`${styles.right} ${f.saldo_proyectado >= 0 ? styles.ingreso : styles.egreso}`}>
                          {fmt(f.saldo_proyectado)}
                        </td>
                        <td className={styles.accion}>
                          <FaEdit
                            title="Editar"
                            style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (f.tipo === "facturado") {
                                setPlanCuotasModal({ factura: f, tipo: "cobro" });
                              } else if (f.tipo === "proveedor") {
                                setPlanCuotasModal({ factura: { ...f, id: f.id }, tipo: "pago" });
                              }
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <FactoringModal
        isOpen={!!facturaFactoring}
        onClose={() => setFacturaFactoring(null)}
        factura={facturaFactoring}
        onSuccess={() => cargarDatos()}
      />

      {planCuotasModal && (
        <PlanCuotasModal
          isOpen={!!planCuotasModal}
          onClose={() => setPlanCuotasModal(null)}
          tipo={planCuotasModal.tipo}
          factura={planCuotasModal.factura}
          onSuccess={() => cargarDatos()}
        />
      )}

      {vincularModal && (
        <VincularMovimientoModal
          show={!!vincularModal}
          onClose={() => setVincularModal(null)}
          movimiento={vincularModal}
          onSuccess={() => cargarDatos()}
        />
      )}

      {confirmarMatchModal && (
        <Modal show={!!confirmarMatchModal} onHide={() => setConfirmarMatchModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "0.95rem" }}>Confirmar vinculación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {/* Movimiento bancario — izquierda */}
              <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 12 }}>
                <div style={{ fontSize: "0.72rem", color: "#888", marginBottom: 4 }}>MOVIMIENTO BANCARIO</div>
                <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                  {movBancarios.find((m) => m.id === confirmarMatchModal.match.movimiento_id)?.descripcion_raw}
                </div>
                <div style={{ color: "#888", fontSize: "0.78rem" }}>{confirmarMatchModal.match.fecha}</div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, marginTop: 6, color: "#27ae60" }}>
                  {fmt(confirmarMatchModal.match.monto)}
                </div>
              </div>
              {/* Proyección — derecha */}
              <div style={{ background: "#f8f9fa", borderRadius: 6, padding: 12 }}>
                <div style={{ fontSize: "0.72rem", color: "#888", marginBottom: 4 }}>PROYECCIÓN</div>
                <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                  {confirmarMatchModal.proyeccion.razon_social_receptor ||
                    confirmarMatchModal.proyeccion.razon_social_emisor}
                </div>
                <div style={{ fontSize: "0.78rem", color: "#666" }}>Folio {confirmarMatchModal.proyeccion.folio}</div>
                <div
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    marginTop: 6,
                    color: confirmarMatchModal.proyeccion.importeEnMoneda < 0 ? "#c0392b" : "#27ae60",
                  }}
                >
                  {fmt(Math.abs(confirmarMatchModal.proyeccion.importeEnMoneda))}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, textAlign: "center", fontSize: "0.78rem", color: "#888" }}>
              Confianza: {Math.round(confirmarMatchModal.match.confianza * 100)}%
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setConfirmarMatchModal(null)}>
              Cancelar
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={async () => {
                try {
                  const { proyeccion, match } = confirmarMatchModal;
                  const esCobro = proyeccion.tipo === "facturado";
                  await client
                    .from("movimientos_bancarios")
                    .update(
                      esCobro ? { factura_sii_emitida_id: proyeccion.id } : { factura_sii_recibida_id: proyeccion.id }
                    )
                    .eq("id", match.movimiento_id);

                  if (!esCobro) {
                    await client
                      .from("facturas_sii_recibidas")
                      .update({ plazopago: match.fecha })
                      .eq("id", proyeccion.id);
                  }

                  toast.success("Vinculación confirmada");
                  setConfirmarMatchModal(null);
                  await cargarDatos();
                } catch {
                  toast.error("Error al vincular");
                }
              }}
            >
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default FlujoCajaV2;
